import { Building, Globe, Link2, Lock, Unlock } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { TextInput } from '@/components/inputs';
import { Text } from '@/components/text';
import { tv } from '@/utils/styles';

export interface ShareFormProps {
  url: string;
  shouldRestrictToOrg?: boolean;
  onClickShareOption: (shareWithOrg: boolean) => void;
  onCopy: () => void;
  orgName: string;
}

const ShareForm = ({ url, onCopy, shouldRestrictToOrg = false, onClickShareOption, orgName }: ShareFormProps) => {
  const [didCopy, setDidCopy] = useState(false);

  const handleClick = (shareWithOrg: boolean) => {
    onClickShareOption(shareWithOrg);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>, shareWithOrg: boolean) => {
    if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      handleClick(shareWithOrg);
    }
  };

  const handleClickCopy = () => {
    setDidCopy(true);
    onCopy();
  };

  useEffect(() => {
    if (didCopy) {
      setTimeout(() => {
        setDidCopy(false);
      }, 1500);
    }
  }, [didCopy]);

  const { base, text, button, buttonIcon, buttonBody, copiedText, intro, linkWrapper } = styles();
  return (
    <section className={base()}>
      <div className="flex flex-col gap-2">
        <Heading as="h3" className={intro()}>
          People with access:
        </Heading>
        <div
          role="button"
          tabIndex={0}
          aria-label={`share with org: ${orgName}`}
          className={button({ selected: shouldRestrictToOrg })}
          onClick={() => handleClick(true)}
          onKeyUp={(event) => handleKeyPress(event, true)}
        >
          <div className={buttonIcon({ className: 'bg-lightBlue-500' })}>
            <Building size={20} />
          </div>
          <div>
            <Text className={buttonBody({ className: 'text-text-secondary' })}>Organisation:</Text>
            <Text className={buttonBody()}>{orgName}</Text>
          </div>
        </div>

        <div
          role="button"
          tabIndex={0}
          aria-label="share with everyone"
          className={button({ selected: !shouldRestrictToOrg })}
          onClick={() => onClickShareOption(false)}
          onKeyUp={(event) => handleKeyPress(event, false)}
        >
          <div className={buttonIcon({ className: 'bg-transport-plane' })}>
            <Globe size={20} />
          </div>
          <Text className={buttonBody()}>Everyone</Text>
        </div>
      </div>
      <div className={text()}>
        {shouldRestrictToOrg ? <Lock size={14} /> : <Unlock size={14} />}
        <Text className="m-0">
          Visible to{shouldRestrictToOrg ? ` people within the ${orgName} organisation` : ' anyone with the link'}
        </Text>
      </div>
      <div className={linkWrapper()}>
        <TextInput value={url} readOnly />
        <div className="flex items-center gap-2">
          <Button
            className="self-start"
            title="Copy"
            type="button"
            onClick={handleClickCopy}
            aria-label="copy url"
            iconRight={<Link2 size={18} />}
            size="small"
          >
            Copy URL
          </Button>
          <Text className={copiedText({ didCopy })}>Copied!</Text>
        </div>
      </div>
    </section>
  );
};

const styles = tv({
  slots: {
    base: 'grid gap-4',
    intro: 'm-0 !text-baseSm',
    text: 'flex items-center gap-1 text-sm leading-tight text-text-secondary',
    button: 'flex cursor-pointer items-center gap-2 rounded-md bg-grey-50 p-2 hover:outline hover:outline-grey-300',
    buttonIcon: 'flex items-center justify-center rounded-full p-2 text-white',
    buttonBody: 'm-0 text-baseSm leading-tight',
    copiedText: 'm-0 text-baseSm text-text-secondary opacity-0 transition duration-200',
    linkWrapper: 'flex flex-col gap-2',
  },
  variants: {
    didCopy: {
      true: {
        copiedText: 'opacity-100',
      },
    },
    selected: {
      true: {
        button: 'bg-grey-100 outline outline-grey-300',
      },
    },
  },
});

export { ShareForm };
